import { useTranslator } from 'tradera/lang/translate';
import { useCountdown } from 'tradera/hooks/use-countdown';
import { formatItemCardTimeLeft } from 'tradera/utils/format-time-left';
import { useFormatUtcDateInSwedishTimeZone } from 'tradera/hooks/use-format-utc-date-in-swedish-timezone';
import {
    formatUtcTimeAsSwedishTimeZone,
    swedishTimeZoneDate,
} from 'tradera/utils/date';
import { useDayJs } from 'tradera/hooks/use-day-js';
import type { Dayjs } from 'dayjs';

type EndTimeLabelProps = {
    className?: string;
    endDate?: Date | string | null;
    isActive?: boolean | null;
    showDateOnEnded?: boolean;
};

const isSameDay = (endDate: Date | string, dayjs: () => Dayjs): boolean => {
    const end = swedishTimeZoneDate(endDate, dayjs);
    const now = swedishTimeZoneDate(new Date(), dayjs);
    return end.diff(now, 'hour') < 24;
};

export const EndTimeLabel = ({
    className,
    endDate,
    isActive,
    showDateOnEnded = false,
}: EndTimeLabelProps) => {
    if (!endDate) return null;

    return isActive ? (
        <EndTimeLabelWithCountDown className={className} endDate={endDate} />
    ) : (
        <EndTimeLabelEnded
            className={className}
            endDate={endDate}
            showDateOnEnded={showDateOnEnded}
        />
    );
};

const EndTimeLabelEnded = ({
    className,
    endDate,
    showDateOnEnded,
}: Pick<EndTimeLabelProps, 'className' | 'endDate' | 'showDateOnEnded'>) => {
    const { t } = useTranslator();
    const formatUtcDateInSwedishTimeZone = useFormatUtcDateInSwedishTimeZone();
    return (
        <span className={className}>
            {showDateOnEnded
                ? formatUtcDateInSwedishTimeZone(endDate)
                : t('vip_ended')}
        </span>
    );
};

const EndTimeLabelWithCountDown = ({
    className,
    endDate,
}: Pick<EndTimeLabelProps, 'className' | 'endDate'>) => {
    const { t } = useTranslator();
    const dayjs = useDayJs();
    const formatUtcDateInSwedishTimeZone = useFormatUtcDateInSwedishTimeZone();
    const {
        timeLeftText,
        isCountdownDone,
        isCountdownEnabled,
        isTimeLeftBelowWarningLimit,
    } = useCountdown(endDate, formatItemCardTimeLeft);

    const time = timeLeftText
        ? timeLeftText
        : endDate
          ? formatUtcDateInSwedishTimeZone(endDate)
          : '';

    const endingSameDay = endDate ? isSameDay(endDate, dayjs) : false;

    return (
        <span className={className}>
            {isCountdownEnabled && isCountdownDone ? (
                <span className={`text-gray-600`}>{t('itemCard_ended')}</span>
            ) : (
                <>
                    {endingSameDay && (
                        <span>
                            {formatUtcTimeAsSwedishTimeZone(endDate, dayjs)}{' '}
                        </span>
                    )}
                    <span
                        className={`text-nowrap ${
                            isTimeLeftBelowWarningLimit ? 'text-danger' : ''
                        }`}>
                        {time}
                    </span>
                </>
            )}
        </span>
    );
};
